import { Options } from "select2";
import { luscinia } from "./luscinia";

// Helper function to get dropdownParent for Bootstrap modal support
function getDropdownParent(element: JQuery): JQuery {
    const $modal = element.closest('.modal');
    return $modal.length ? $modal : $(document.body);
}

// User image formatting function (example/portal)
luscinia.select2FormatUser = function (o) {
    if (!o) return '';
    if (!o.id)
        return o.text; // optgroup
    else
        return $('<span><img width="20" height="20" src="/UserImage/' + o.id + '"/> ' + o.text + '</span>')[0];
};

// Format style for select2 rows adding a bootstrap label style
luscinia.select2FormatBootstrapStyle = function (o) {
    if (!o) return '';
    if (o.style)
        return $('<span class="label label-' + o.style + '">' + o.text + '</span>')[0];
    else if (o.id)
        return $('<span class="label label-' + o.id + '">' + o.text + '</span>')[0];
    else
        return o.text; // optgroup
};

// Default
luscinia.select2FormatDefault = function (o) {
    if (!o) return '';
    return o.text;
};

// (Re)initialize an ajax enabled select2 element
luscinia.initializeAjaxSelect2 = function (element: JQuery<HTMLElement>) {
    var url = constructAjaxSelect2Url(element);
    var multiple = false;
    var minimumInputLength = 0;
    var emptyline = "";
    var formatFunction: any;

    if (element.attr("multiple")) {
        multiple = true;
    }

    if (element.attr("minimumInputLength")) {
        minimumInputLength = parseInt(element.attr("minimumInputLength") || "0", 10);
    }

    if (element.attr('data-luscinia-select-emptyline')) {
        emptyline = element.attr('data-luscinia-select-emptyline') || "";
    }

    if (element.attr('data-luscinia-select-format')) {
        formatFunction = luscinia.getFunctionByName(element.attr('data-luscinia-select-format'), window);
    }

    // Get initial value for pre-selection
    const initialValue = element.val();
    let select2Promise: JQuery.jqXHR | undefined;

    const select2Options: Options = {
        theme: 'bootstrap-5',
        dropdownParent: getDropdownParent(element),
        ajax: {
            url: url,
            dataType: "json",
            delay: 250,
            data: function (params) {
                return { search: params.term };
            },
            processResults: function (data) {
                if (emptyline !== "") {
                    data.unshift({ id: "", text: emptyline });
                }
                return { results: data };
            }
        },
        multiple: multiple,
        minimumInputLength: minimumInputLength,
        width: "100%",
        allowClear: emptyline !== "",
        placeholder: emptyline !== "" ? { id: "", text: emptyline } : undefined
    };

    // Only set template functions if they're defined
    if (formatFunction) {
        select2Options.templateResult = formatFunction;
        select2Options.templateSelection = formatFunction;
        select2Options.escapeMarkup = function (m: string) { return m; };
    }

    // Initialize select2 with 4.x API
    element.select2(select2Options);

    // Select2 4.x AJAX mode doesn't sync DOM <option> selected state properly.
    // When switching selections, the old option keeps selected=true and the new one gets selected=false.
    // This causes display issues. We manually sync the DOM to match Select2's internal state.
    element.on('select2:select', function(e) {
        const data = e.params?.data;
        if (data?.id !== undefined) {
            // Ensure option exists
            let $option = element.find(`option[value="${data.id}"]`);
            if (!$option.length) {
                $option = $(new Option(data.text, data.id, false, false));
                element.append($option);
            }
            // Sync DOM: deselect all, select the chosen one
            element.find('option').prop('selected', false);
            $option.prop('selected', true);
        }
    });

    // Handle clear button: deselect all options and reset the value
    element.on('select2:clear', function() {
        element.find('option').prop('selected', false);
        element.val(null).trigger('change');
    });

    // Load initial selection if there's a value
    if (initialValue) {
        select2Promise = $.ajax({
            type: "POST",
            url: url,
            dataType: 'json',
            contentType: "application/json",
            data: JSON.stringify({ id: initialValue })
        }).done(function (data) {
            if (data) {
                // Create the option with selected=true and append it
                const option = new Option(data.text, data.id, true, true);
                element.append(option).trigger('change');
            }
        }).fail(function () {
            if (emptyline) {
                const option = new Option(emptyline, "", false, true);
                element.append(option).trigger('change.select2');
            }
        });
    }

    return select2Promise;
};

// (Re)initialize an inline select2 element
luscinia.initializeInlineSelect2 = function (element: JQuery<HTMLElement>) {
    var minimumInputLength = 0;
    var formatFunction: ((data) => JQuery | string) | undefined = undefined;

    if (element.attr("minimumInputLength")) {
        minimumInputLength = parseInt(element.attr("minimumInputLength") || "0", 10);
    }

    if (element.attr('data-luscinia-select-format')) {
        formatFunction = luscinia.getFunctionByName(element.attr('data-luscinia-select-format'), window);
    }

    element.select2({
        theme: 'bootstrap-5',
        dropdownParent: getDropdownParent(element),
        templateResult: formatFunction,
        templateSelection: formatFunction,
        escapeMarkup: function (m) { return m; },
        minimumInputLength: minimumInputLength,
        width: "100%"
    });

    if (element.attr("readonly") !== undefined) {
        element.prop("disabled", true);
    }
};

function constructAjaxSelect2Url(element: JQuery<HTMLElement>): string {
    let url = element.attr('data-luscinia-ajax-select-url') as string;

    const regex = /\{([^}]+)\}/; // This will match anything inside {}
    const matches = url.match(regex);
    if (matches && matches.length > 1) {
        const placeholderValue = matches[1];
        const inputElement = getInputElement(placeholderValue);
        if (!$(inputElement).data('eventAssigned')) {
            $(inputElement).on('change', function () {
                $(element).select2('destroy');
                luscinia.initializeAjaxSelect2(element);
            });
            $(inputElement).data('eventAssigned', true);
        }
        if (inputElement) {
            let value = (inputElement as HTMLInputElement).value;
            if (value === "") {
                value = null;
            }
            url = url.replace(`{${placeholderValue}}`, value);
        }
    }
    return url;
}

function getInputElement(placeholderValue: string): Element {
    return document.querySelector(`input[name="parameter-${placeholderValue}"]`) || document.querySelector(`input[name="${placeholderValue}"]`);
}
